import classNames from 'classnames';
import ReactSelect, { ClassNamesConfig, SingleValue } from 'react-select';

import { PaymentMethodLogo } from './PaymentMethodLogo';
import { PaymentMethodOption } from './PaymentMethodOption';
import { PaymentMethodValue } from './PaymentMethodValue';
import styles from './SelectPaymentMethod.module.css';
import selectFieldStyles from '../../../forms/select/Select.module.css';
import { TSelectedPayment } from '../EarlyRepayment.reducer';

export type PaymentOption = {
    type: string;
    value: string | number;
    cardLastDigits?: string;
    issuer?: string;
    brand?: string;
    label?: string;
};

const selectClassNames: ClassNamesConfig<PaymentOption, false> = {
    control: (state) =>
        classNames(styles.control, {
            [styles.controlDisabled]: state.isDisabled
        }),
    valueContainer: (state) =>
        classNames(styles.valueContainer, {
            [styles.valueContainerWithLogo]: state.hasValue
        }),
    singleValue: () => styles.singleValue,
    placeholder: () => styles.placeholder,
    menu: () => styles.menu,
    indicatorSeparator: () => styles.indicatorSeparator,
    option: () => styles.option
};

const selectComponents = {
    Option: PaymentMethodOption,
    SingleValue: PaymentMethodValue
};

export const SelectPaymentMethod = ({
    value,
    onChange,
    selectRepaymentMethod,
    options,
    disabled,
    'data-testid': dataTestId
}: {
    value: TSelectedPayment | null;
    onChange: (value: TSelectedPayment) => void;
    selectRepaymentMethod: (value: TSelectedPayment) => void;
    options?: PaymentOption[];
    disabled?: boolean;
    'data-testid'?: string;
}) => {
    const safeOptions = options ?? [];
    const matchedOption = safeOptions.find(
        (option) => String(option.value) === value?.value
    );
    const selectedOption = matchedOption
        ? { ...matchedOption, ...value }
        : undefined;

    const handleChange = (selectedValue: SingleValue<PaymentOption>) => {
        if (!selectedValue) return;
        const payment: TSelectedPayment = {
            type: selectedValue.type,
            value: String(selectedValue.value ?? ''),
            cardLastDigits: selectedValue.cardLastDigits ?? '',
            issuer: selectedValue.issuer ?? '',
            brand: selectedValue.brand ?? '',
            label: selectedValue.label ?? ''
        };
        onChange(payment);
        selectRepaymentMethod(payment);
    };

    return (
        <div
            className={`${selectFieldStyles.SelectContainer} ${styles.SelectPaymentMethod}`}
            data-testid={dataTestId}
        >
            {selectedOption && (
                <div className={styles.SelectedLogo}>
                    <PaymentMethodLogo
                        type={selectedOption.type}
                        issuer={selectedOption.issuer}
                        logoStyle={styles.icon}
                    />
                </div>
            )}
            <ReactSelect<PaymentOption, false>
                className={classNames(styles.SelectPaymentMethodDropDown, {
                    [styles.SelectPaymentMethodDropDownWithLogo]: selectedOption
                })}
                isClearable={false}
                components={selectComponents}
                onChange={handleChange}
                value={selectedOption}
                options={safeOptions}
                isDisabled={disabled}
                getOptionValue={(option) => String(option.value)}
                getOptionLabel={(option) => option.label ?? ''}
                classNames={selectClassNames}
            />
        </div>
    );
};
